import { GameObject, Rectangle, Vector2 } from '../../../types/game.types';
import { RenderManager } from '../../../core/RenderManager';
import { AudioManager } from '../../../core/AudioManager';
import { Rideable } from '../interfaces/Rideable';

export type HazardType = 'snake' | 'otter' | 'crocodile';

export class Hazard implements GameObject, Rideable {
  public id: string;
  public active: boolean = true;
  public position: Vector2;
  public velocity: Vector2 = { x: 0, y: 0 };
  public size: Vector2;
  
  private speed: number;
  private hazardType: HazardType;
  private renderManager: RenderManager;
  private audioManager: AudioManager;
  private gameWidth: number;
  
  // Visual properties
  private width: number = 32;
  private height: number = 24;
  private animationTimer: number = 0;
  
  // Crocodile specific properties
  private mouthTimer: number = 0;
  private readonly MOUTH_CYCLE: number = 4.0; // 4 seconds cycle
  private readonly MOUTH_WARNING: number = 2.5; // Start warning at 2.5 seconds

  constructor(
    id: string,
    x: number,
    y: number,
    speed: number,
    hazardType: HazardType,
    renderManager: RenderManager,
    audioManager: AudioManager,
    gameWidth: number
  ) {
    this.id = id;
    this.position = { x, y };
    this.speed = speed;
    this.hazardType = hazardType;
    this.renderManager = renderManager;
    this.audioManager = audioManager;
    this.gameWidth = gameWidth;
    
    this.setupProperties();
    this.size = { x: this.width, y: this.height };
  }

  private setupProperties(): void {
    switch (this.hazardType) {
      case 'snake':
        this.width = 64; // Long snake
        this.height = 16;
        break;
      case 'otter':
        this.width = 32;
        this.height = 24;
        break;
      case 'crocodile':
        this.width = 80; // Large crocodile
        this.height = 28;
        // Randomize mouth cycle start time
        this.mouthTimer = Math.random() * this.MOUTH_CYCLE;
        break;
    }
  }

  public update(deltaTime: number): void {
    // Move horizontally
    this.position.x += this.speed * deltaTime * 60; // Normalize for 60 FPS
    
    // Update animation timer
    this.animationTimer += deltaTime;
    
    // Update crocodile mouth behavior
    if (this.hazardType === 'crocodile') {
      this.mouthTimer += deltaTime;
      
      if (this.mouthTimer >= this.MOUTH_CYCLE) {
        this.mouthTimer = 0;
      }
    }
    
    // Mark as inactive if far off screen
    if (this.speed > 0 && this.position.x > this.gameWidth + 200) {
      this.active = false;
    } else if (this.speed < 0 && this.position.x < -200) {
      this.active = false;
    }
  }

  public getPosition(): Vector2 {
    return { ...this.position };
  }

  public getBounds(): Rectangle {
    return {
      x: this.position.x,
      y: this.position.y,
      width: this.width,
      height: this.height
    };
  }

  public getSpeed(): number {
    return this.speed;
  }

  public isSafe(): boolean {
    // Crocodiles are safe when mouth is closed, others are never safe
    if (this.hazardType === 'crocodile') {
      return this.mouthTimer < this.MOUTH_WARNING;
    }
    return false;
  }

  public isWarning(): boolean {
    // Only crocodiles give warnings before snapping
    return this.hazardType === 'crocodile' && 
           this.mouthTimer >= this.MOUTH_WARNING && 
           this.mouthTimer < this.MOUTH_WARNING + 0.5;
  }

  public checkCollision(otherBounds: Rectangle): boolean {
    // Hazards are dangerous unless explicitly safe
    if (this.hazardType === 'crocodile' && this.isSafe()) {
      // Safe crocodiles act like logs
      const myBounds = this.getBounds();
      
      return myBounds.x < otherBounds.x + otherBounds.width &&
             myBounds.x + myBounds.width > otherBounds.x &&
             myBounds.y < otherBounds.y + otherBounds.height &&
             myBounds.y + myBounds.height > otherBounds.y;
    }
    
    return false; // Other hazards don't provide platform support
  }

  public checkDangerCollision(otherBounds: Rectangle): boolean {
    // Check if the hazard will harm the frog
    const myBounds = this.getBounds();
    
    const collision = myBounds.x < otherBounds.x + otherBounds.width &&
                     myBounds.x + myBounds.width > otherBounds.x &&
                     myBounds.y < otherBounds.y + otherBounds.height &&
                     myBounds.y + myBounds.height > otherBounds.y;

    if (!collision) return false;

    // Crocodiles are only dangerous when mouth is open
    if (this.hazardType === 'crocodile') {
      return this.mouthTimer >= this.MOUTH_WARNING + 0.5;
    }

    // Snakes and otters are always dangerous
    return true;
  }

  public render(ctx: CanvasRenderingContext2D): void {
    // Note: Offsets will be handled by the game when calling this method
    switch (this.hazardType) {
      case 'snake':
        this.renderSnake(this.position.x, this.position.y);
        break;
      case 'otter':
        this.renderOtter(this.position.x, this.position.y);
        break;
      case 'crocodile':
        this.renderCrocodile(this.position.x, this.position.y);
        break;
    }
  }

  public renderWithOffset(ctx: CanvasRenderingContext2D, offsetX: number, offsetY: number): void {
    const renderX = offsetX + this.position.x;
    const renderY = offsetY + this.position.y;
    
    switch (this.hazardType) {
      case 'snake':
        this.renderSnake(renderX, renderY);
        break;
      case 'otter':
        this.renderOtter(renderX, renderY);
        break;
      case 'crocodile':
        this.renderCrocodile(renderX, renderY);
        break;
    }
  }

  private renderSnake(x: number, y: number): void {
    const segments = Math.floor(this.width / 8);
    const segmentWidth = this.width / segments;
    
    for (let i = 0; i < segments; i++) {
      const segmentX = x + i * segmentWidth;
      const waveOffset = Math.sin(this.animationTimer * 4 + i * 0.5) * 3;
      const segmentY = y + 8 + waveOffset;
      
      // Snake segment color alternates between dark and light green
      const segmentColor = i % 2 === 0 ? '#228B22' : '#32CD32';
      
      this.renderManager.drawEllipse(
        segmentX,
        segmentY - 4,
        segmentWidth * 0.8,
        4,
        segmentColor,
        true
      );
    }
    
    // Snake head (first segment)
    const headY = y + 8 + Math.sin(this.animationTimer * 4) * 3;
    this.renderManager.drawEllipse(
      x,
      headY - 6,
      segmentWidth,
      6,
      '#8B0000', // Dark red
      true
    );
    
    // Eyes
    this.renderManager.drawEllipse(x + 2, headY - 4, 1, 1, '#FF0000', true);
    this.renderManager.drawEllipse(x + 6, headY - 4, 1, 1, '#FF0000', true);
  }

  private renderOtter(x: number, y: number): void {
    const centerX = x + this.width / 2;
    const centerY = y + this.height / 2;
    
    // Otter body (brown)
    const bodyColor = '#8B4513';
    
    // Main body
    this.renderManager.drawEllipse(
      centerX - this.width * 0.4,
      centerY - this.height * 0.3,
      this.width * 0.4,
      this.height * 0.3,
      bodyColor,
      true
    );
    
    // Head
    const headX = centerX + (this.speed > 0 ? this.width * 0.3 : -this.width * 0.3);
    const headY = centerY - 4;
    this.renderManager.drawEllipse(headX - 8, headY - 6, 8, 6, bodyColor, true);
    
    // Eyes
    this.renderManager.drawEllipse(headX - 6, headY - 4, 1, 1, '#000000', true);
    this.renderManager.drawEllipse(headX - 2, headY - 4, 1, 1, '#000000', true);
    
    // Whiskers
    this.renderManager.drawLine(headX - 8, headY - 2, headX - 12, headY - 3, '#000000', 1);
    this.renderManager.drawLine(headX - 8, headY, headX - 12, headY, '#000000', 1);
    this.renderManager.drawLine(headX - 8, headY + 2, headX - 12, headY + 1, '#000000', 1);
    
    // Tail
    const tailX = centerX - (this.speed > 0 ? this.width * 0.4 : -this.width * 0.4);
    const tailWave = Math.sin(this.animationTimer * 8) * 4;
    this.renderManager.drawEllipse(tailX - 6, centerY + tailWave - 2, 6, 2, bodyColor, true);
  }

  private renderCrocodile(x: number, y: number): void {
    const centerX = x + this.width / 2;
    const centerY = y + this.height / 2;
    
    // Crocodile body (dark green)
    let bodyColor = '#006400';
    
    // Warning color (flash red when about to snap)
    if (this.isWarning()) {
      const flashSpeed = 15;
      const flash = Math.sin(this.animationTimer * flashSpeed) > 0;
      bodyColor = flash ? '#8B0000' : '#006400';
    }
    
    // Main body
    this.renderManager.fillRect(x + 10, y + 6, this.width - 20, this.height - 12, bodyColor);
    
    // Head
    const headX = centerX + (this.speed > 0 ? this.width * 0.3 : -this.width * 0.4);
    const headWidth = 20;
    const headHeight = 16;
    
    this.renderManager.fillRect(headX - headWidth/2, centerY - headHeight/2, headWidth, headHeight, bodyColor);
    
    // Eyes
    this.renderManager.drawEllipse(headX - 6, centerY - 8, 2, 2, '#FFFF00', true);
    this.renderManager.drawEllipse(headX + 2, centerY - 8, 2, 2, '#FFFF00', true);
    
    // Mouth/jaw
    const mouthOpen = this.mouthTimer >= this.MOUTH_WARNING + 0.5;
    if (mouthOpen) {
      // Open mouth with teeth
      this.renderManager.fillRect(headX - 8, centerY - 2, 16, 6, '#000000');
      // Teeth
      for (let i = 0; i < 4; i++) {
        this.renderManager.fillRect(headX - 6 + i * 3, centerY - 2, 1, 3, '#FFFFFF');
        this.renderManager.fillRect(headX - 6 + i * 3, centerY + 1, 1, 3, '#FFFFFF');
      }
    } else {
      // Closed mouth
      this.renderManager.drawLine(headX - 8, centerY, headX + 8, centerY, '#000000', 2);
    }
    
    // Tail with ridges
    const tailX = centerX - (this.speed > 0 ? this.width * 0.3 : -this.width * 0.3);
    for (let i = 0; i < 3; i++) {
      const ridgeX = tailX - i * 6;
      const ridgeHeight = 4 - i;
      this.renderManager.fillRect(ridgeX - 1, centerY - ridgeHeight, 2, ridgeHeight * 2, bodyColor);
    }
  }
}